import { useState, useCallback, useEffect } from 'react'
import { useFooterState } from './useFooterRegistration'

interface UseAiButtonReturn {
  showCtaModal: boolean
  showCtaInsteadOfButton: boolean
  showCta: () => void
  hideCta: () => void
  handleCtaTryIt: () => void
  handleCtaClose: () => void
  hasInteractedWithCta: boolean
  hasFooterBeneath: boolean
}

const CTA_INTERACTION_KEY = 'ai-button-cta-interacted'

export const useAiButton = (
  onTryIt?: () => void,
  onClose?: () => void,
  showCtaInitially: boolean = false
): UseAiButtonReturn => {
  const [showCtaModal, setShowCtaModal] = useState(false)
  const [hasInteractedWithCta, setHasInteractedWithCta] = useState(() => {
    // Check if user has interacted with CTA before
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CTA_INTERACTION_KEY) === 'true'
    }
    return false
  })

  const showCtaInsteadOfButton = showCtaInitially && !hasInteractedWithCta

  // Always use the global footer state
  const { hasFooter } = useFooterState()

  const showCta = useCallback(() => {
    setShowCtaModal(true)
  }, [])

  const hideCta = useCallback(() => {
    setShowCtaModal(false)
  }, [])

  const markCtaAsInteracted = useCallback(() => {
    setHasInteractedWithCta(true)
    if (typeof window !== 'undefined') {
      localStorage.setItem(CTA_INTERACTION_KEY, 'true')
    }
  }, [])

  const handleCtaTryIt = useCallback(() => {
    markCtaAsInteracted()
    hideCta()
    onTryIt?.()
  }, [markCtaAsInteracted, hideCta, onTryIt])

  const handleCtaClose = useCallback(() => {
    markCtaAsInteracted()
    hideCta()
    onClose?.()
  }, [markCtaAsInteracted, hideCta, onClose])

  // Show CTA initially if requested and user hasn't interacted before
  useEffect(() => {
    if (showCtaInitially && !hasInteractedWithCta) {
      showCta()
    }
  }, [showCtaInitially, hasInteractedWithCta, showCta])

  return {
    showCtaModal,
    showCtaInsteadOfButton,
    showCta,
    hideCta,
    handleCtaTryIt,
    handleCtaClose,
    hasInteractedWithCta,
    hasFooterBeneath: hasFooter,
  }
}

export default useAiButton
